import React, { memo, useMemo } from 'react';
import { Image } from '@core/js/components/common/Image';
import {
    useJackpotsConfiguration
} from '../../../../../../../../../js/components/system/App/hooks/useJackpotsConfiguration/useJackpotsConfiguration';
import { useGameImageFallback } from '@theme/js/hooks/swiss/useGameImageFallback';
import style from './GameInfoTopSectionDesktop.scss';
import GamesJackpot from '@theme/js/components/main/Games/GamesJackpot/GamesJackpot';
import { FavoriteIcon } from 'js/components/common/Game/FavoriteIcon';
import { Title } from '../../../components/Title/Title';
import { DemoButton, PlayButton } from 'js/components/common/Game/Buttons';
import { useGameImageUrls, useLiveGameConfig, useLiveGames } from '@core/js/hooks/games';
import { usePrevious } from '@core/js/hooks/usePrevious';
import { useGetGreenTubeAndSkyWindEnablad } from '@theme/js/hooks/swiss/useGetGreenTubeAndSkyWindEnablad';

const GameInfoTopSectionDesktop = ({
      image,
      name,
      showFavorite,
      code,
      hideButtons,
      gameLaunchType,
      liveLaunchAlias,
      demoModeSupport,
      isScheduledLiveGame,
      t
}) => {
    const liveGameConfig = useLiveGameConfig(liveLaunchAlias);
    const isLiveGame = liveGameConfig?.launchAlias;
    const { dynamicImageUrl, openingDateTime } = liveGameConfig || {};
    const imageUrls = useGameImageUrls({ code, dynamicImageUrl, isLiveGame });
    // prevImageUrl is needed to avoid image blinking to default while new live image is loading
    const prevImageUrl = usePrevious(imageUrls[0]);
    const gameImages = isLiveGame && prevImageUrl
        ? [imageUrls[0], prevImageUrl, ...imageUrls.slice(1)]
        : imageUrls;
    const gameImageBackGround = gameImages.map(url => `url("${url}")`).join(', ');

    const liveLaunchAliases = useMemo(() => ({
        ...liveLaunchAlias && { liveLaunchAliases: [liveLaunchAlias] }
    }), [liveLaunchAlias]);

    useLiveGames(liveLaunchAliases);

    const {
        showJackpots,
        showGuaranteedHitTime
    } = useJackpotsConfiguration();

    const { showGreenTubeJackpots, showSkyWindJackpots } = useGetGreenTubeAndSkyWindEnablad();

    const {
        landscapeImageSrc,
        landscapeImageHandleError
    } = useGameImageFallback({
        image,
        gameCode: code
    });

    return (
        <div className={style.top}>
            {isLiveGame ? (<div className={`${style.logo} ${style.liveLogo}`}>
                <div className={style.logoImage} style={{ backgroundImage: gameImageBackGround }} />
                {isScheduledLiveGame && (
                    <div className={style.scheduledLiveGame}>
                        {openingDateTime
                            ? (
                                <>
                                    <span>{t('playtech.mobile.system.liveGame.startsAt')}</span>
                                    <span>{openingDateTime}</span>
                                </>
                            )
                            : <span>{t('playtech.mobile.system.liveGame.startsSoon')}</span>
                        }
                    </div>
                )}
            </div>) : (<div className={style.logo}>
                <Image
                    className={style.logoImage}
                    src={isLiveGame ? gameImageBackGround : landscapeImageSrc}
                    onError={landscapeImageHandleError}
                    alt={name}
                />
            </div>)}
            <div className={style.card}>
                <div className={style.cardInner}>
                    <GamesJackpot
                        gameCode={code}
                        className={style.jackpot}
                        showGuaranteedHitTime={showGuaranteedHitTime}
                        isShowJackpot={showJackpots}
                        showGreenTube={showGreenTubeJackpots}
                        showSkyWind={showSkyWindJackpots}
                        isRegularJackpot={showJackpots}
                        isInGamePortlet={false}
                    />
                    {showFavorite && <FavoriteIcon isFeatured={true} className={style.favorite} code={code}/>}
                    <Title>{name}</Title>
                </div>
                {!hideButtons && !isScheduledLiveGame && (
                    <div className={style.buttons}>
                        {demoModeSupport && !isLiveGame && <DemoButton code={code} name={name} gameLaunchType={gameLaunchType}/>}
                        <PlayButton code={code}
                                    name={name}
                                    className={style.real}
                                    gameLaunchType={gameLaunchType}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(GameInfoTopSectionDesktop);
